import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link as RouterLink } from 'react-router-dom';
import { paymentReviewApi } from '../services/api';

/**
 * The operator queue for payment cases the system refuses to decide by itself
 * (booking contract §8).
 *
 * Everything in here exists because guessing would move or strand real money: an unknown PayPal
 * outcome, an authorization discovered after the service was due to start (which needs BOTH
 * parties to attest that the service happened), and a completion the provider never reported.
 * Until this screen existed the endpoints were only reachable by hand-rolled API calls.
 *
 * Two rules are deliberately enforced by the SERVER, not here:
 *  - which outcomes an item may take (`allowedOutcomes`, re-checked against the live order), so a
 *    stale queue cannot resolve something that has since moved;
 *  - the attestation identities, which are derived from the order — this form only records the
 *    CHANNEL each party confirmed through.
 */

const TYPE_LABELS = {
  RECONCILIATION_REQUIRED: 'Unknown PayPal outcome',
  MANUAL_RECOVERY: 'Manual recovery',
  COMPLETION_OVERDUE: 'Completion not reported',
};

const OUTCOME_LABELS = {
  RESOLVED_NOTE_ONLY: 'Resolve with a note only',
  CONFIRM_ATTESTED: 'Confirm — both parties attested',
  VOID_AND_CANCEL: 'Release the hold and cancel',
  COMPLETE_OFFLINE: 'Mark complete (settled outside the app)',
  CANCEL_OFFLINE: 'Cancel the booking (settled outside the app)',
};

const STATUS_COLORS = { OPEN: 'warning', CLAIMED: 'info', RESOLVED: 'success' };

const PaymentReviews = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [resolving, setResolving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await paymentReviewApi.list(0, 100));
    } catch (e) {
      setError(e?.message || 'Could not load the payment review queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const claim = async (item) => {
    setBusyId(item.id);
    try {
      await paymentReviewApi.claim(item.id);
      setToast('Item claimed');
      await load();
    } catch (e) {
      setError(e?.message || 'Could not claim this item');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Payment reviews</Typography>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        These bookings are waiting on a human decision because the system refuses to guess with
        money. Claim an item before working it so two operators cannot resolve the same case.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Nothing is waiting for review.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Opened</TableCell>
                <TableCell>Booking</TableCell>
                <TableCell>Why</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Attestations</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{new Date(item.createdAt).toLocaleString()}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      component={RouterLink}
                      to={`/orders/${item.orderRequestId}`}
                    >
                      #{item.orderRequestId}
                    </Button>
                  </TableCell>
                  <TableCell>{TYPE_LABELS[item.type] || item.type}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={item.status}
                      color={STATUS_COLORS[item.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {/* Only meaningful for CONFIRM_ATTESTED, but showing it always makes it
                        obvious WHY that outcome is unavailable on an item missing one. */}
                    <Typography variant="caption" display="block">
                      Customer: {item.customerAttestedAt ? '✓' : '—'}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Provider: {item.providerAttestedAt ? '✓' : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {item.status === 'OPEN' && (
                      <Button
                        size="small"
                        onClick={() => claim(item)}
                        disabled={busyId === item.id}
                      >
                        Claim
                      </Button>
                    )}
                    <Tooltip
                      title={
                        item.allowedOutcomes?.length
                          ? ''
                          : 'The server allows no outcome for this item in its current state'
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          sx={{ ml: 1 }}
                          onClick={() => setResolving(item)}
                          disabled={!item.allowedOutcomes?.length}
                        >
                          Resolve
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ResolveDialog
        item={resolving}
        onClose={() => setResolving(null)}
        onResolved={async (message) => {
          setResolving(null);
          setToast(message);
          await load();
        }}
        onError={setError}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
      />
    </Box>
  );
};

/**
 * Resolve one item.
 *
 * The outcome list comes from the item itself rather than being hardcoded, so this form can
 * never offer something the server will reject — and the attestation channels appear only for
 * the outcome that actually requires them.
 */
const ResolveDialog = ({ item, onClose, onResolved, onError }) => {
  const [outcome, setOutcome] = useState('');
  const [note, setNote] = useState('');
  const [customerChannel, setCustomerChannel] = useState('');
  const [providerChannel, setProviderChannel] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setOutcome(item?.allowedOutcomes?.length === 1 ? item.allowedOutcomes[0] : '');
    setNote('');
    setCustomerChannel('');
    setProviderChannel('');
  }, [item]);

  if (!item) return null;

  const needsAttestations = outcome === 'CONFIRM_ATTESTED';
  const canSubmit = !!outcome &&
    (!needsAttestations || (customerChannel.trim() && providerChannel.trim()));

  const submit = async () => {
    setSaving(true);
    try {
      await paymentReviewApi.resolve(item.id, {
        outcome,
        note: note.trim() || null,
        customerAttestationChannel: needsAttestations ? customerChannel.trim() : null,
        providerAttestationChannel: needsAttestations ? providerChannel.trim() : null,
      });
      await onResolved(`Booking #${item.orderRequestId} resolved`);
    } catch (e) {
      onError(e?.message || 'Could not resolve this item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Resolve booking #{item.orderRequestId}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {TYPE_LABELS[item.type] || item.type}
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="outcome-label">Outcome</InputLabel>
          <Select
            labelId="outcome-label"
            label="Outcome"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
          >
            {(item.allowedOutcomes || []).map((o) => (
              <MenuItem key={o} value={o}>{OUTCOME_LABELS[o] || o}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {needsAttestations && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Confirm only once BOTH parties have told you the service actually happened. AniPaw
              never showed the provider a confirmed booking, so nothing else proves it did.
            </Alert>
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label="How the customer confirmed"
              placeholder="phone / email / chat"
              value={customerChannel}
              onChange={(e) => setCustomerChannel(e.target.value)}
              helperText="The channel only — the identity is taken from the booking."
            />
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label="How the provider confirmed"
              placeholder="phone / email / chat"
              value={providerChannel}
              onChange={(e) => setProviderChannel(e.target.value)}
            />
          </>
        )}

        {outcome === 'VOID_AND_CANCEL' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This releases the customer's authorization and cancels the booking. If PayPal does not
            confirm the release, the item stays claimed rather than recording a release that may
            not have happened.
          </Alert>
        )}

        {outcome === 'COMPLETE_OFFLINE' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Marks the booking complete and opens the customer's review. No money moves.
          </Alert>
        )}

        {outcome === 'CANCEL_OFFLINE' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Cancels the booking and attributes the cancellation to the provider. No money moves.
          </Alert>
        )}

        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Note (audited)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit || saving}>
          {saving ? <CircularProgress size={18} /> : 'Resolve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentReviews;
